import { useEffect } from "react";
import clsx from "clsx";
import {
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import InputMask from "react-input-mask";
import {
  BLANK,
  FROM_TEMPLATE as TEMPLATE,
  EXISTING_PLAN_PROFILE,
  DAILY_PROFILE,
} from "../../../consts/strings";
import { getDateFromTimestamp } from "../../../utils/date";
import { DayChips } from "../../DayChips/DayChips";
import { View } from "../../View";

const useStyles = makeStyles({
  content: {
    maxWidth: "350px",
    display: "flex",
    flexDirection: "column",
    lineHeight: "16px",
    padding: "0px",
    fontSize: "12px",
    marginTop: "10px",
    gap: "10px",
    overflow: "hidden",
  },
  labelContainer: {
    display: "flex",
  },
  label: {
    marginRight: "5px",
  },
  bold: {
    fontWeight: "bold",
  },
  errorLabel: {
    marginLeft: "10px",
  },
  radioContainer: {
    marginTop: "-5px",
  },
  radioButton: {
    height: "30px",
    padding: "0px",
    "& .MuiFormControlLabel-label": {
      fontSize: "12px",
    },
  },
  daysContainer: {
    marginTop: "-5px",
  },
  dayChips: {
    marginTop: "10px",
  },
  disabled: {
    pointerEvents: "none",
    opacity: "0.5",
  },
});

export const CreatePlanSecondStepContent = ({
  date,
  name,
  basedOn,
  selectedPlan,
  allowDriverFromPlan,
  driverSource,
  onDriverSourceChange,
  selectedDay,
  setSelectedDay,
  optionalDisabled,
  dutyTime,
  onDutyTimeChange,
  amTimeInDepot,
  onAmTimeInDepotChange,
  pmTimeInDepot,
  onPmTimeInDepotChange,
  errors,
}) => {
  const styles = useStyles();

  useEffect(() => {
    if (!optionalDisabled) return;
    onDutyTimeChange(null);
    onAmTimeInDepotChange(null);
    onPmTimeInDepotChange(null);
  }, [
    optionalDisabled,
    onDutyTimeChange,
    onAmTimeInDepotChange,
    onPmTimeInDepotChange,
  ]);

  const getBasedOnLabel = () => {
    if (basedOn === BLANK) return "Blank";
    const { templateName, activeDate } = selectedPlan.value;
    if (basedOn === TEMPLATE) return `Template (${templateName})`;
    else if (activeDate)
      return `Exsisting Plan (${getDateFromTimestamp(
        activeDate
      )}(${templateName}))`;
    else return `Exsisting Plan (${templateName})`;
  };

  return (
    <View className={styles.content}>
      <View className={styles.labelContainer}>
        <View className={clsx(styles.label, styles.bold)}>Plan Name:</View>
        {name}
        {date ? ` (${getDateFromTimestamp(date)})` : ""}
      </View>
      <View className={styles.labelContainer}>
        <View className={clsx(styles.label, styles.bold)}>Based On:</View>
        {getBasedOnLabel()}
      </View>
      <View className={clsx(styles.label, styles.bold)}>
        Selected Driver Profile Source:
      </View>

      {allowDriverFromPlan && (
        <View>
          <View className={styles.radioContainer}>
            <FormControl component="fieldset" fullWidth>
              <RadioGroup
                aria-label="driver-source"
                name="driverSource"
                value={driverSource}
                onChange={({ target }) => onDriverSourceChange(target.value)}
              >
                <FormControlLabel
                  className={styles.radioButton}
                  value={EXISTING_PLAN_PROFILE}
                  control={<Radio color={"primary"} size={"small"} />}
                  label="Existing Plan Profile"
                />
                <FormControlLabel
                  className={styles.radioButton}
                  value={DAILY_PROFILE}
                  control={<Radio color={"primary"} size={"small"} />}
                  label="Daily Profile"
                />
              </RadioGroup>
            </FormControl>
          </View>
        </View>
      )}

      {(!allowDriverFromPlan || driverSource === DAILY_PROFILE) && (
        <View className={styles.daysContainer}>
          <View className={clsx(styles.label)}>Driver Schedule Day</View>

          <View className={styles.dayChips}>
            <DayChips
              selected={selectedDay}
              onDaySelected={(value) => {
                setSelectedDay(
                  selectedDay === value.value ? null : value.value
                );
              }}
            />
          </View>
        </View>
      )}

      <View className={styles.labelContainer}>
        <View
          className={clsx(
            styles.label,
            styles.bold,
            optionalDisabled && styles.disabled
          )}
        >{`Standardise Duty Hours (Optional):`}</View>
      </View>
      <View className={clsx(optionalDisabled && styles.disabled)}>
        {`Option to set all Duty Time, Am & PM times In Depot to the same amount for all drivers`}
      </View>

      <InputMask
        mask="99:99"
        disabled={optionalDisabled}
        onChange={({ target }) => onDutyTimeChange(target.value)}
        value={dutyTime}
      >
        {() => (
          <TextField
            className={clsx(optionalDisabled && styles.disabled)}
            variant={"outlined"}
            label={"Duty Time"}
            error={!!errors.dutyTime}
            helperText={errors.dutyTime}
            FormHelperTextProps={{
              style: { marginLeft: "0px" },
            }}
          />
        )}
      </InputMask>

      <InputMask
        mask="99:99"
        disabled={optionalDisabled}
        onChange={({ target }) => onAmTimeInDepotChange(target.value)}
        value={amTimeInDepot}
      >
        {() => (
          <TextField
            className={clsx(optionalDisabled && styles.disabled)}
            variant={"outlined"}
            label={"Am Time In Depot"}
            error={!!errors.amTimeInDepot}
            helperText={errors.amTimeInDepot}
            FormHelperTextProps={{
              style: { marginLeft: "0px" },
            }}
          />
        )}
      </InputMask>

      <InputMask
        mask="99:99"
        disabled={optionalDisabled}
        onChange={({ target }) => onPmTimeInDepotChange(target.value)}
        value={pmTimeInDepot}
      >
        {() => (
          <TextField
            className={clsx(optionalDisabled && styles.disabled)}
            variant={"outlined"}
            label={"Pm Time In Depot"}
            error={!!errors.pmTimeInDepot}
            helperText={errors.pmTimeInDepot}
            FormHelperTextProps={{
              style: { marginLeft: "0px" },
            }}
          />
        )}
      </InputMask>
    </View>
  );
};
